import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
} from "@mui/icons-material";
import { Box, Paper, Stack, Typography } from "@mui/material";
import * as arrays from "common/arrays";
import * as ids from "common/ids";
import Component from "components/Component";
import IconButton from "components/common/IconButton";
import TextField from "components/common/TextField";
import * as labels from "config.labels";
import { useCallback } from "react";
import { useDrag, useDrop } from "react-dnd";
import { useDispatch, useSelector } from "react-redux";
import * as endorsementsStore from "store/endorsements";
import * as staticDataHooks from "store/hooks/staticDataHooks";

const DND_TYPE_SPECIAL_CONDITION = "SPECIAL_CONDITION";

const Entry = ({ index, value, onUpdate, onDelete, onDrop }) => {
  const [, dragRef, dragPreviewRef] = useDrag(() => ({
    type: DND_TYPE_SPECIAL_CONDITION,
    item: { index },
  }));

  const [, dropRef] = useDrop(
    () => ({
      accept: DND_TYPE_SPECIAL_CONDITION,
      drop: (item) => onDrop(item.index),
    }),
    [onDrop]
  );

  return (
    <Stack
      direction={"row"}
      spacing={2}
      justifyContent={"flex-start"}
      alignItems={"center"}
      ref={(el) => {
        dragRef(el);
        dropRef(el);
        dragPreviewRef(el);
      }}
    >
      <Typography
        width={20}
        align={"right"}
        fontWeight={"bold"}
        sx={{ padding: 2 }}
      >
        {index + 1}
      </Typography>
      {value.type === "CUSTOM" ? (
        <TextField
          hiddenLabel
          multiline
          value={value?.text ?? ""}
          onChange={(e) =>
            onUpdate({
              ...value,
              text: e.target.value,
            })
          }
        />
      ) : (
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"flex-start"}
          sx={{ width: "100%" }}
        >
          <Typography sx={{ padding: "12px" }}>{value?.text ?? ""}</Typography>
        </Stack>
      )}
      <Stack
        direction={"row"}
        spacing={0}
        justifyContent={"flex-start"}
        alignItems={"center"}
      >
        <IconButton icon={DeleteIcon} onClick={onDelete} />
        <DragIndicatorIcon />
      </Stack>
    </Stack>
  );
};

const SelectedSpecialConditions = ({
  entries,
  updateEntry,
  deleteEntry,
  moveEntry,
}) => {
  return (
    <Stack direction={"column"} spacing={1}>
      {entries.map((entry, index) => (
        <Entry
          key={index}
          index={index}
          value={entry}
          onUpdate={updateEntry(entry.key)}
          onDelete={deleteEntry(entry.key)}
          onDrop={moveEntry(index)}
        />
      ))}
    </Stack>
  );
};

const AvailableSpecialConditions = ({ addEntry }) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  return (
    <Paper sx={{ width: "100%" }}>
      <Stack
        direction={"column"}
        spacing={2}
        sx={{
          padding: 2,
        }}
      >
        <Box>
          <IconButton
            icon={AddIcon}
            onClick={() => addEntry({ text: "" })}
            tooltip={translateLabel(
              labels.SPECIAL_CONDITIONS_ADD_CUSTOM_TOOLTIP,
              {
                default: "Add Custom",
              }
            )}
          />
        </Box>
      </Stack>
    </Paper>
  );
};

export const SpecialConditions = ({ layerId }) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  const dispatch = useDispatch();

  const rawEntries = useSelector(endorsementsStore.selectInput)
    ?.specialConditionsByLayer?.[layerId];
  const entries = rawEntries ?? [];

  const setEntries = useCallback(
    (newEntries) =>
      dispatch(
        endorsementsStore.setLayerSpecialConditions({
          layerId,
          specialConditions: newEntries,
        })
      ),
    [dispatch, layerId]
  );

  const addEntry = (value) => {
    const entry = value?.key
      ? {
          ...value,
        }
      : {
          ...value,
          type: "CUSTOM",
          key: ids.makeNewId(),
        };

    setEntries([...entries, entry]);
  };

  const updateEntry = (key) => (value) =>
    setEntries(
      entries.map((entry) =>
        entry.key === key ? { ...entry, ...value } : entry
      )
    );

  const deleteEntry = (key) => () =>
    setEntries(entries.filter((entry) => entry.key !== key));

  const moveEntry = (toIndex) => (fromIndex) =>
    setEntries(arrays.move(entries, { fromIndex, toIndex }));

  return (
    <Component
      title={translateLabel(labels.SPECIAL_CONDITIONS_TITLE, {
        default: "Special Conditions",
      })}
      subtitle={translateLabel(labels.SPECIAL_CONDITIONS_SUBTITLE, {
        default: "",
      })}
    >
      <Stack direction={"column"} spacing={2}>
        <SelectedSpecialConditions
          entries={entries}
          addEntry={addEntry}
          updateEntry={updateEntry}
          deleteEntry={deleteEntry}
          moveEntry={moveEntry}
        />
        <AvailableSpecialConditions addEntry={addEntry} />
      </Stack>
    </Component>
  );
};

export default SpecialConditions;
